import {AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {PricingArea} from '../../data/pricing-area';
import Handsontable from 'handsontable';
import {Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {ProjectCalcService} from '../../data/project-calc.service';
import {ColumnSettings} from 'handsontable/settings';

@Component({
  selector: 'app-subtotals-table-sheet',
  templateUrl: './subtotals-table-sheet.component.html',
  styleUrls: ['./subtotals-table-sheet.component.scss']
})
export class SubtotalsTableSheetComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input()
  area: PricingArea;

  @Input()
  pricingId: number

  @ViewChild('table')
  tableEl: ElementRef;

  table: Handsontable;

  customTableLoading = true;
  progressBarLoading = true;

  subTotalSubj: Subscription;

  subtotalTotalsSet = new Set()
  subtotalsToEditMapLabels = new Map()
  subtotalsCellClasses = new Map()

  constructor(
    private projectCalcService: ProjectCalcService
  ) {
    this.subtotalsToEditMapLabels.set('Premium 1, %', 'subtotalDiscount1')
    this.subtotalsToEditMapLabels.set('Premium 2, %', 'subtotalDiscount2');

    this.subtotalsToEditMapLabels.set('Premium 1, $', 'subtotalDiscount1')
    this.subtotalsToEditMapLabels.set('Premium 2, $', 'subtotalDiscount2');

    this.subtotalsCellClasses.set('Premium 1, %', 'htEditable')
    this.subtotalsCellClasses.set('Premium 2, %', 'htEditable')
    this.subtotalsCellClasses.set('Premium 1, $', 'htEditable')
    this.subtotalsCellClasses.set('Premium 2, $', 'htEditable')

    this.subtotalsCellClasses.set('Premium 1, $ value', 'htOrangeCell');
    this.subtotalsCellClasses.set('Premium 2, $ value', 'htOrangeCell');

    this.subtotalsCellClasses.set('Cabinetry Total', 'htOrangeCell');
    this.subtotalsCellClasses.set('Install', 'htOrangeCell');
    this.subtotalsCellClasses.set('Cabinetry/Delivery/Install Grand Total', 'htOrangeCell');

    this.subtotalTotalsSet.add('Cabinetry Total');
    this.subtotalTotalsSet.add('Install');
    this.subtotalTotalsSet.add('Cabinetry/Delivery/Install Grand Total');
  }

  ngOnInit(): void {
    this.subTotalSubj = this.projectCalcService.subTotalSubj.pipe(debounceTime(500)).subscribe(areaId => {
      this.fillSubtotalsData(areaId)
    })
    this.projectCalcService.subTotalSubj.next(this.area.id)
  }

  fillSubtotalsData(areaId: number) {
    this.progressBarLoading = true;

    this.projectCalcService.getSubtotals(this.pricingId, areaId).subscribe(subtotalsData => {
      this.table.updateData(subtotalsData.items)


      // specific
      this.table.updateSettings({
        cells: (row, col, prop) => {
          const cellProperties = {
            readOnly: true,
            className: 'subtotalsCell',
            numericFormat: {
              pattern: '$0,0.00',
              culture: 'en-US' // this is the default culture, set up for USD
            },
          } as ColumnSettings;

          if (typeof prop === 'string') {
            const fieldName = this.table.getDataAtCell(row, 0)
            if (this.subtotalsCellClasses.has(fieldName) && prop === 'value') {
              if (this.subtotalsCellClasses.get(fieldName) === 'htEditable') { // EDITABLE
                cellProperties.readOnly = false;
                cellProperties.numericFormat = {
                  pattern: ''
                }
              }
              cellProperties.className += (' ' + this.subtotalsCellClasses.get(fieldName))

            }
            if (this.subtotalTotalsSet.has(fieldName)) {
              cellProperties.className += ' htTotal'
            }
          }
          return cellProperties;
        }
      })
      this.customTableLoading = false;
      this.progressBarLoading = false;
    }, () => {
      this.customTableLoading = false;
      this.progressBarLoading = false;
    })
  }

  fillSubtotalsTable() {
    this.table = new Handsontable(this.tableEl.nativeElement, {
      data: [],
      height: 'auto',
      rowHeaders: true,
      rowHeaderWidth: 30,
      colHeaders: ['Name', 'Value'],
      columns: [
        {data: 'name', type: 'text', readOnly: true},
        {
          data: 'value', type: 'numeric', readOnly: true,
          numericFormat: {
            pattern: '$0,0.00',
            culture: 'en-US' // this is the default culture, set up for USD
          },
        }
      ],
      colWidths: [280, 120],
      // manualColumnResize: true,
      licenseKey: 'non-commercial-and-evaluation', // for non-commercial use only
      contextMenu: false,
      afterChange: (changes) => {
        if (changes) {
          const tempArea = {
            id: this.area.id
          } as PricingArea;

          const row = changes[0][0]
          const val = changes[0][3]
          const colName = this.table.getDataAtCell(row, 0)

          if (this.subtotalsToEditMapLabels.has(colName)) {
            const fieldName = this.subtotalsToEditMapLabels.get(colName)
            tempArea[fieldName] = val
          }

          this.projectCalcService.updateArea(this.area.id, tempArea).subscribe(() => {
            this.projectCalcService.subTotalSubj.next(this.area.id)
          })
        }
      },
      afterSelectionEnd: () => {
        this.countSelectedCells()
      }
    });
  }

  countSelectedCells() {
    this.projectCalcService.countSelectedCells(this.table)
  }

  ngAfterViewInit() {
    this.fillSubtotalsTable()
  }

  ngOnDestroy() {
    if (this.subTotalSubj) {
      this.subTotalSubj.unsubscribe()
      this.subTotalSubj = null;
    }
  }

}
